import { useEffect, useRef, useState } from "react";
import { PaperPlaneTilt, MagnifyingGlass } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const ROLE_LABEL = { admin: "Admin", professeur: "Prof", etudiant: "Étudiant" };

export default function Messages() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const endRef = useRef(null);

  useEffect(() => { api.get("/messages/contacts").then((r) => setContacts(r.data)); }, []);
  const loadConv = (c) => { setActive(c); api.get(`/messages/${c.id}`).then((r) => setMsgs(r.data)); };
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => api.get(`/messages/${active.id}`).then((r) => setMsgs(r.data)), 5000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/messages", { recipient_id: active.id, content: text });
      setMsgs((m) => [...m, data]); setText("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const shown = contacts.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Communication</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Messagerie</h1>
      </div>

      <div className="grid h-[70vh] grid-cols-1 overflow-hidden rounded-xl border border-border bg-card md:grid-cols-3">
        <div className="border-r border-border md:col-span-1">
          <div className="border-b border-border p-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-9" data-testid="contact-search" />
            </div>
          </div>
          <div className="h-full overflow-y-auto">
            {shown.map((c) => (
              <button key={c.id} onClick={() => loadConv(c)} className={`flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left hover:bg-secondary ${active?.id === c.id ? "bg-secondary" : ""}`} data-testid={`contact-${c.id}`}>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{c.name[0]}</div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{c.name}</p>
                  <Badge variant="secondary" className="text-[10px]">{ROLE_LABEL[c.role]}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:col-span-2">
          {!active ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Sélectionnez un contact pour discuter</div>
          ) : (
            <>
              <div className="border-b border-border p-4"><p className="font-semibold">{active.name}</p></div>
              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {msgs.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{m.content}</div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="flex gap-2 border-t border-border p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message..." data-testid="message-input" />
                <Button onClick={send} className="bg-primary hover:bg-primary/90" data-testid="message-send"><PaperPlaneTilt size={18} /></Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
